import { useEffect, useState } from 'react';
import { VsjStepHeader } from './VsjStepHeader';
import { useVsjFlow } from './vsj-flow';
import { locatePlace, LocateResponse } from './vsj-locate-api';

type LocateStatus = 'locating' | 'located' | 'off_network' | 'error';

/**
 * Location confirmation. The map lives in VsjMapStage (persistent across
 * location → environment) — this step overlays the header and sheet on top.
 *
 * On mount, calls places/locate to snap the point to the network, retrieve
 * the placeToken, and illuminate the confirmed cuadra on the map.
 */
export function VsjLocationStep(): JSX.Element {
  const { place, locateResult, setLocateResult, goTo } = useVsjFlow();

  const [locateStatus, setLocateStatus] = useState<LocateStatus>(
    locateResult ? 'located' : 'locating'
  );
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    // Cached result from a previous visit (back-navigation): skip re-fetch.
    if (locateResult !== null) {
      setLocateStatus('located');
      return;
    }

    let cancelled = false;
    setLocateStatus('locating');

    locatePlace(place.lng, place.lat)
      .then((res: LocateResponse) => {
        if (cancelled) return;
        if (res.status === 'off_network') {
          setLocateStatus('off_network');
          return;
        }
        setLocateResult({
          placeToken: res.placeToken,
          confirmedGeometry: res.confirmedGeometry,
          streetName: res.place.streetName,
          referenceLabel: res.place.referenceLabel,
        });
        setLocateStatus('located');
      })
      .catch(() => {
        if (!cancelled) setLocateStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey]);

  const displayAddress = locateResult?.streetName ?? place.address;
  const displayMeta =
    locateResult?.referenceLabel ??
    [place.block, place.neighborhood, place.city].filter(Boolean).join(' · ');

  if (locateStatus === 'off_network') {
    return (
      <div className="vsj-step vsj-step--map">
        <VsjStepHeader step={1} onBack={() => goTo('home')} overlay />
        <div className="vsj-map-space" />
        <div className="vsj-location-sheet">
          <p className="vsj-location-question">Fuera del área cubierta</p>
          <p className="vsj-off-network-msg">
            Este punto queda fuera del área con cobertura. Elegí una dirección dentro de San Juan
            capital.
          </p>
          <button className="vsj-btn-primary" onClick={() => goTo('home')}>
            Cambiar dirección
          </button>
        </div>
      </div>
    );
  }

  const isLocating = locateStatus === 'locating';

  return (
    <div className="vsj-step vsj-step--map">
      <VsjStepHeader step={1} onBack={() => goTo('home')} overlay />

      <div className="vsj-map-space" />

      <div className="vsj-location-sheet">
        <p className="vsj-location-question">¿Es esta la propiedad?</p>
        <p className="vsj-location-address">{displayAddress}</p>
        <p className="vsj-location-meta">{displayMeta}</p>

        {locateStatus === 'error' && (
          <p className="vsj-locate-error">
            No se pudo verificar la ubicación.{' '}
            <button className="vsj-locate-retry" onClick={() => setRetryKey(k => k + 1)}>
              Reintentar
            </button>
          </p>
        )}

        <button
          className="vsj-btn-primary"
          onClick={() => {
            if (!isLocating) goTo('environment');
          }}
          disabled={isLocating}
        >
          {isLocating ? 'Verificando…' : 'Sí, es acá'}
        </button>
        <button className="vsj-btn-ghost" onClick={() => goTo('home')}>
          Cambiar dirección
        </button>
      </div>
    </div>
  );
}
